import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectHomeSearch,
  setSearchParameters,
  toggleAdvancedSearch,
  selectHasCriteria,
  setHomePagination,
  clearSearchParameters,
} from "../../redux/slices/homeSearchSlice";
import { withTranslation } from "react-i18next";
import useShipments from "../../hooks/useShipments";
import useLabels from "../../hooks/useLabels";
import DetailedTracksTable from "../../components/DetailedTracksTable/DetailedTracksTable";
import LabelModal from "../../components/Modals/LabelModal/LabelModal";
import Search from "../../components/Search/Search";
import ListExportModal from "../../components/Modals/ListExportModal/ListExportModal";
import { ListExportScope } from "../../components/Modals/ListExportModal/ListExportModal";
import { exportData } from "../../utils/exportShipmentUtils";

function Home() {
  const dispatch = useDispatch();
  const searchParameters = useSelector(selectHomeSearch);
  const hasCriteria = useSelector(selectHasCriteria);
  const { page, size } = searchParameters;

  const advancedSearchVisible = !!searchParameters.startValue;

  const [labelModalVisible, setLabelModalVisible] = useState(false);
  const [selectedShipmentCode, setSelectedShipmentCode] = useState(null);

  const {
    fetchShipments,
    fetchShipmentsForExport,
    shipmentData,
    fetchShipmentPod,
    getSearchOptions,
    searchOptions,
  } = useShipments(null);

  const { getLabels, labelDetails, resetLabelsErrors } = useLabels();

  const [exportState, setExportState] = useState({
    visible: false,
    fileType: null,
    telemetryVariant: "full",
  });

  const checkValue = (value) => (value === "" ? null : value);

  const handleDateRangeChange = (field, value) => {
    dispatch(setSearchParameters({ [field]: value }));
  };

  const handleSearchInputChange = (event) => {
    const value = event.target.value.toUpperCase();
    dispatch(
      setSearchParameters({
        value,
        [searchParameters.type]: value.trim(),
      }),
    );
  };

  const handleAdvancedInputChange = (field, value) => {
    dispatch(
      setSearchParameters({
        [field]: value.trim() === "" ? null : value.trim(),
      }),
    );
  };

  const handleMultiSelectChange = (field, selectedValues) => {
    dispatch(
      setSearchParameters({
        [field]: Array.isArray(selectedValues)
          ? [...new Set(selectedValues)]
          : [],
      }),
    );
  };

  const handleTypeChange = (value) => {
    dispatch(
      setSearchParameters({
        type: value,
        value: "",
        [searchParameters.type]: "",
      }),
    );
  };

  const handleToggleAdvancedSearch = () => {
    dispatch(toggleAdvancedSearch(!advancedSearchVisible));
  };

  const handleSearchShipments = (e) => {
    e.preventDefault();

    dispatch(
      setSearchParameters({
        searchWasMade: true,
        shipmentCode: checkValue(searchParameters.shipmentCode),
        imei: checkValue(searchParameters.imei),
        numRefClient: checkValue(searchParameters.numRefClient),
        onParameterPicked: true,
        onDateRangePicked: true,
        page: 1,
      }),
    );
    dispatch(setHomePagination({ page: 1, size }));
  };

  const handleOpenLabelModal = (shipmentCode) => {
    setSelectedShipmentCode(shipmentCode);
    setLabelModalVisible(true);
  };

  const handleCloseLabelModal = () => {
    resetLabelsErrors();
    setLabelModalVisible(false);
    setSelectedShipmentCode(null);
  };

  const handleClearSearch = () => {
    dispatch(clearSearchParameters());
  };

  const openExportModal = (fileType, telemetryVariant) => {
    setExportState({
      visible: true,
      fileType,
      telemetryVariant,
    });
  };

  const closeExportModal = () => {
    setExportState({
      visible: false,
      fileType: null,
      telemetryVariant: "full",
    });
  };

  const requestExportData = async (_fileType, maxExport) => {
    const parameters = searchParameters;

    return await fetchShipmentsForExport({
      shipmentCode: parameters.shipmentCode,
      imei: parameters.imei,
      numRefClient: parameters.numRefClient,
      start: parameters.startValue,
      end: parameters.endValue,
      clientNumber: parameters.clientNumber,
      labellerCode: parameters.labellerCode,
      boxType: parameters.boxType,
      temperatureTrazability: parameters.temperatureTrazability,
      temperatureRange: parameters.temperatureRange,
      maxSize: maxExport,
    });
  };

  const doExport = (data, fileType) => {
    exportData(data, fileType, exportState.telemetryVariant);
  };

  useEffect(() => {
    getSearchOptions();

    if (searchParameters.searchWasMade) {
      const parameters = searchParameters;

      fetchShipments(
        parameters.shipmentCode,
        parameters.imei,
        parameters.numRefClient,
        parameters.startValue,
        parameters.endValue,
        parameters.clientNumber,
        parameters.labellerCode,
        parameters.boxType,
        parameters.temperatureTrazability,
        parameters.temperatureRange,
        size || 10,
        page || 1,
        false,
      );
    }
  }, []);

  return (
    <div className="Home">
      <Search
        searchParameters={searchParameters}
        handleDateRangeChange={handleDateRangeChange}
        handleSearchShipments={handleSearchShipments}
        handleSearchInputChange={handleSearchInputChange}
        handleTypeChange={handleTypeChange}
        advancedSearchVisible={advancedSearchVisible}
        handleToggleAdvancedSearch={handleToggleAdvancedSearch}
        searchOptions={searchOptions}
        handleAdvancedInputChange={handleAdvancedInputChange}
        handleMultiSelectChange={handleMultiSelectChange}
        hasCriteria={hasCriteria}
        handleClearSearch={handleClearSearch}
      />
      {searchParameters.searchWasMade && (
        <DetailedTracksTable
          searchParameters={searchParameters}
          fetchShipments={fetchShipments}
          fetchShipmentPod={fetchShipmentPod}
          shipmentData={shipmentData}
          currentPage={page}
          pageSize={size}
          onOpenLabelModal={handleOpenLabelModal}
          onExport={openExportModal}
        />
      )}

      {labelModalVisible && (
        <LabelModal
          shipmentCode={selectedShipmentCode}
          getLabels={getLabels}
          labelsData={labelDetails.data}
          labelsIsLoading={labelDetails.isLoading}
          labelsErrors={labelDetails.errors}
          handleClose={handleCloseLabelModal}
        />
      )}

      <ListExportModal
        listSummary={shipmentData}
        exportState={exportState}
        onClose={closeExportModal}
        onRequestData={requestExportData}
        onExport={doExport}
        exportScope={ListExportScope.SHIPMENT_LIST}
      />
    </div>
  );
}

export default withTranslation()(Home);
